// apps/web/src/features/admin/CatalogDesignationDialog.tsx
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Autocomplete,
  FormControl,
  FormLabel,
  FormControlLabel,
  Radio,
  RadioGroup,
  MenuItem,
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { CATALOG_SECTIONS, type CatalogSection, type BillingMode } from '@altyor-kronos/shared-types';
import {
  validateCatalogDesignationForm,
  type CatalogDesignationFormValues,
} from './validators';
import { useResponsive } from '../../hooks/useResponsive';

export interface ExistingCatalogDesignation {
  id: string;
  section: CatalogSection;
  name: string;
  variants: string[];
  billingMode: BillingMode;
}

interface Props {
  open: boolean;
  editing: ExistingCatalogDesignation | null;
  availableVariants: string[];
  onSubmit: (values: CatalogDesignationFormValues) => Promise<void>;
  onClose: () => void;
}

const EMPTY: CatalogDesignationFormValues = {
  name: '',
  section: 'rd',
  billingMode: 'hourly',
  variants: [],
};

export default function CatalogDesignationDialog({
  open,
  editing,
  availableVariants,
  onSubmit,
  onClose,
}: Props) {
  const { t } = useTranslation();
  const { isMobile } = useResponsive();
  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isSubmitting },
    reset,
    setError,
  } = useForm<CatalogDesignationFormValues>({ defaultValues: EMPTY });

  const [variants, setVariants] = useState<string[]>([]);

  useEffect(() => {
    if (open) {
      const vals = editing
        ? { name: editing.name, section: editing.section, billingMode: editing.billingMode, variants: editing.variants }
        : EMPTY;
      reset(vals);
      setVariants(vals.variants);
    }
  }, [open, editing, reset]);

  const onFormSubmit = handleSubmit(async (values) => {
    const withVariants = { ...values, variants };
    const validation = validateCatalogDesignationForm(withVariants);
    let hasError = false;
    (['name', 'section'] as const).forEach((field) => {
      if (validation[field]) {
        setError(field, { message: validation[field] });
        hasError = true;
      }
    });
    if (hasError) return;
    await onSubmit(withVariants);
    onClose();
  });

  return (
    <Dialog open={open} onClose={onClose} fullScreen={isMobile} maxWidth="sm" fullWidth>
      <DialogTitle>
        {editing ? t('admin.designations.edit') : t('admin.designations.new')}
      </DialogTitle>
      <form onSubmit={onFormSubmit} noValidate>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label={t('admin.designations.name')}
              fullWidth
              autoFocus
              {...register('name')}
              error={Boolean(errors.name)}
              helperText={errors.name?.message}
            />
            <Controller
              control={control}
              name="section"
              render={({ field }) => (
                <TextField
                  {...field}
                  label={t('admin.designations.section')}
                  select
                  fullWidth
                  disabled={Boolean(editing)}
                  error={Boolean(errors.section)}
                  helperText={
                    errors.section?.message ??
                    (editing ? t('admin.designations.sectionLocked') : undefined)
                  }
                >
                  {CATALOG_SECTIONS.map((s) => (
                    <MenuItem key={s} value={s}>
                      {t(`projects.designations.sections.${s}`)}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
            <Controller
              control={control}
              name="billingMode"
              render={({ field }) => (
                <FormControl>
                  <FormLabel>{t('admin.designations.billingMode')}</FormLabel>
                  <RadioGroup row {...field}>
                    <FormControlLabel
                      value="hourly"
                      control={<Radio />}
                      label={t('projects.designations.billingMode.hourly')}
                    />
                    <FormControlLabel
                      value="fixed"
                      control={<Radio />}
                      label={t('projects.designations.billingMode.fixed')}
                    />
                  </RadioGroup>
                </FormControl>
              )}
            />
            <Autocomplete
              multiple
              freeSolo
              filterSelectedOptions
              options={availableVariants}
              value={variants}
              onChange={(_, newValue) => setVariants(newValue as string[])}
              slotProps={{ chip: { size: 'small' } }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('admin.designations.variants')}
                  helperText={t('admin.designations.variantHelp')}
                />
              )}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} variant="outlined" color="secondary" disabled={isSubmitting}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" variant="contained" color="primary" disabled={isSubmitting}>
            {t('common.save')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
